import type { WebhookEndpoint } from '@compass/client/types/webhook';
import { Loader2 } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface RotateSecretDialogProps {
  endpoint: WebhookEndpoint;
  isPending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Confirms a signing-secret rotation. The new secret is shown once by the
 * caller (ShowSigningSecretDialog). The previous secret keeps signing during
 * an overlap window so receivers can switch without dropping events.
 */
export function RotateSecretDialog({
  endpoint,
  isPending,
  onConfirm,
  onClose,
}: RotateSecretDialogProps) {
  return (
    <Dialog
      open
      onOpenChange={next => {
        if (!next && !isPending) onClose();
      }}
    >
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Rotate signing secret</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{endpoint.name || 'This endpoint'}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2 text-sm text-muted-foreground">
          <p>A new signing secret is created and shown once.</p>
          <p>
            The current secret keeps working for a while, and every request is signed with both
            until it expires, so you can update your receiver without missing events.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isPending}
            data-analytics-id="webhooks:endpoint:rotate-secret"
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Rotate secret
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
